import React, { useEffect, useMemo, useState } from "react";
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import {
  Accordion,
  Table,
  Form,
  Button,
  Spinner,
  Alert,
  Modal,
  Badge,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { portalApi } from "../../redux/api/portalApi";
import { refreshCriticalResource } from '../../redux/slices/criticalDataSlice';
import { replaceRealtimeDropdownValues } from '../../redux/slices/dropdownSlice';
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import AddDropdownModal from "./AddDropdownModal";
import { getDropdownUsage } from "./dropdownUsageRegistry.generated";
import "./AdminDropdowns.css";

const isDropdownActive = (value) => (
  value === true ||
  value === 1 ||
  String(value ?? "").trim().toLowerCase() === "true"
);

const generateValueFromDisplayText = (text) => {
  if (!text) return "";
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export const buildBulkDropdownChanges = (existingItems, desiredRows) => {
  const orderedExisting = [...existingItems].sort((left, right) => (
    (Number(left.SortOrder) || Number.MAX_SAFE_INTEGER) -
    (Number(right.SortOrder) || Number.MAX_SAFE_INTEGER)
  ));
  const usedIds = new Set();
  const assignments = desiredRows.map((row) => ({ row, item: null }));
  const normalizeLabel = (value) => String(value ?? "").trim().toLocaleLowerCase();

  // Preserve record identity when an existing label is merely reordered.
  assignments.forEach((assignment) => {
    const match = orderedExisting.find((item) => (
      !usedIds.has(String(item.ID)) &&
      normalizeLabel(item.DisplayText) === normalizeLabel(assignment.row.displayText)
    ));
    if (match) {
      assignment.item = match;
      usedIds.add(String(match.ID));
    }
  });

  // Remaining lines are edits of remaining rows, in their prior order.
  const unmatchedExisting = orderedExisting.filter((item) => !usedIds.has(String(item.ID)));
  assignments.forEach((assignment) => {
    if (assignment.item || unmatchedExisting.length === 0) return;
    assignment.item = unmatchedExisting.shift();
    usedIds.add(String(assignment.item.ID));
  });

  const updates = [];
  const creates = [];
  assignments.forEach(({ row, item }, index) => {
    const sortOrder = index + 1;
    if (!item) {
      creates.push({ ...row, sortOrder });
      return;
    }

    const displayText = row.displayText.trim();
    const displayTextChanged = item.DisplayText !== displayText;
    const updatedItem = {
      ID: item.ID,
      DropdownType: item.DropdownType,
      Value: displayTextChanged ? generateValueFromDisplayText(displayText) : item.Value,
      DisplayText: displayText,
      SortOrder: sortOrder,
      IsActive: item.IsActive,
    };

    if (displayTextChanged || Number(item.SortOrder) !== sortOrder) {
      updates.push(updatedItem);
    }
  });

  const deletes = orderedExisting.filter((item) => !usedIds.has(String(item.ID)));
  return { updates, creates, deletes };
};

function DropdownValues() {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newEntries, setNewEntries] = useState({});
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [bulkEditState, setBulkEditState] = useState({
    show: false,
    type: "",
    values: [],
  });
  const [deleteState, setDeleteState] = useState({
    show: false,
    id: null,
    displayText: "",
  });
  const [openAccordion, setOpenAccordion] = useState(null);

  const dropdownValuesUrl = apiUrls?.dropdownValues || "/dropdown-values";

  // Utility function to group data by DropdownType and sort by SortOrder
  const groupDataByType = (data) => {
    return data.reduce((acc, item) => {
      const type = item.DropdownType?.trim() || "Unknown";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(item);
      acc[type] = acc[type].sort((a, b) => a.SortOrder - b.SortOrder);
      return acc;
    }, {});
  };

  // Initial render of dropdown values
  useRealtimeDraft(Boolean(editItem || showAddDropdown || bulkEditState.show || deleteState.show || pendingAction));
  useGuardedRealtimeList({ apiKey: 'dropdownValues', blocked: loading || refreshing,
    apply: rows => { setData(rows); setGroupedData(groupDataByType(rows)); } });
  useEffect(() => {
    fetchDropdownValues();
  }, [/* eslint-disable-line react-hooks/exhaustive-deps */]);

  // Function to fetch dropdown values from API using Redux
  const fetchDropdownValues = async ({ background = false } = {}) => {
    if (background || data.length > 0) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    const selectedAccordion = openAccordion;

    try {
      const response = await dispatch(
        refreshCriticalResource('dropdownValues')
      ).unwrap();
      
      if (Array.isArray(response)) {
        // Keep forms in this tab current even before the backend event arrives.
        dispatch(replaceRealtimeDropdownValues(response));
        setData(response);
        setGroupedData(groupDataByType(response));
      } else {
        setError("Unexpected response format from the server");
      }
      
      if (selectedAccordion) {
        setOpenAccordion(selectedAccordion);
      }
    } catch (err) {
      console.error("Error fetching dropdown values:", err);
      setError(`Failed to fetch dropdown values: ${err.message || err}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  // Function to update sort orders to ensure unique values and send PUT requests
  const updateSortOrder = async (items, newSortOrder, itemId, dropdownType) => {
    const typeItems = items
      .filter((item) => item.DropdownType === dropdownType)
      .map((item) => ({ ...item }))
      .sort((left, right) => (
        (Number(left.SortOrder) || Number.MAX_SAFE_INTEGER) -
        (Number(right.SortOrder) || Number.MAX_SAFE_INTEGER)
      ));

    let targetItem = null;
    if (itemId !== null) {
      const currentIndex = typeItems.findIndex((item) => String(item.ID) === String(itemId));
      if (currentIndex >= 0) {
        [targetItem] = typeItems.splice(currentIndex, 1);
      }
    }

    const insertionIndex = Math.max(
      0,
      Math.min(Number.parseInt(newSortOrder, 10) - 1, typeItems.length)
    );
    const orderedItems = [...typeItems];
    orderedItems.splice(insertionIndex, 0, targetItem || { isNewEntryPlaceholder: true });

    const changedItems = [];
    const updatedById = new Map();
    orderedItems.forEach((item, index) => {
      if (item.isNewEntryPlaceholder) return;
      const nextItem = { ...item, SortOrder: index + 1 };
      updatedById.set(String(nextItem.ID), nextItem);
      if (Number(item.SortOrder) !== nextItem.SortOrder) {
        changedItems.push(nextItem);
      }
    });

    // Persist only rows whose sequential position actually changed.
    const putPromises = changedItems.map(async (item) => {
        const updatedItem = {
          ID: item.ID,
          DropdownType: item.DropdownType,
          Value: item.Value,
          DisplayText: item.DisplayText,
          SortOrder: item.SortOrder,
          IsActive: item.IsActive,
        };

        try {
          await dispatch(
            portalApi.endpoints.updateDropdownValue.initiate({
              url: dropdownValuesUrl,
              data: updatedItem,
            })
          ).unwrap();
        } catch (error) {
          console.error("Error updating item:", error);
          throw error; // Propagate the error
        }
    });

    try {
      await Promise.all(putPromises);
    } catch (err) {
      console.error("Error updating sort orders:", err);
      setError(`Failed to update sort orders: ${err.message || err}`);
      throw err; // Propagate the error to the caller
    }

    return items.map((item) => (
      item.DropdownType === dropdownType
        ? updatedById.get(String(item.ID)) || item
        : item
    ));
  };

  const getDefaultNewEntry = (type) => ({
    displayText: "",
    sortOrder: groupedData[type]?.length ? String(groupedData[type].length + 1) : "1",
    isActive: true,
  });

  const getNewEntry = (type) => newEntries[type] || getDefaultNewEntry(type);

  const updateNewEntry = (type, updates) => {
    setNewEntries((prev) => ({
      ...prev,
      [type]: {
        ...getNewEntry(type),
        ...updates,
      },
    }));
  };

  const clearNewEntry = (type) => {
    setNewEntries((prev) => ({
      ...prev,
      [type]: getDefaultNewEntry(type),
    }));
  };

  // POST: Add new value
  const handleSubmit = async (e, type) => {
    e.preventDefault();
    const currentNewEntry = getNewEntry(type);
    
    if (!currentNewEntry.displayText || currentNewEntry.displayText.trim() === "") {
      setError("Display text cannot be empty");
      return;
    }
    
    if (!currentNewEntry.sortOrder || isNaN(Number.parseInt(currentNewEntry.sortOrder, 10))) {
      setError("Sort order must be a valid number");
      return;
    }

    try {
      setPendingAction(`add:${type}`);
      setError(null);
      setNotice("");
      const typeItemCount = groupedData[type]?.length || 0;
      const resolvedSortOrder = Math.max(
        1,
        Math.min(Number.parseInt(currentNewEntry.sortOrder, 10), typeItemCount + 1)
      );
      // Update sort orders and handle any non-sequential sort orders
      const updatedData = await updateSortOrder(
        data,
        resolvedSortOrder,
        null,
        type
      );
      setData(updatedData);

      const postDataObj = {
        dropdownType: type,
        displayText: currentNewEntry.displayText,
        value: generateValueFromDisplayText(currentNewEntry.displayText),
        sortOrder: resolvedSortOrder,
        isActive: currentNewEntry.isActive,
      };

      await dispatch(
        portalApi.endpoints.createDropdownValue.initiate({
          url: dropdownValuesUrl,
          data: postDataObj,
        })
      ).unwrap();
      
      await fetchDropdownValues();
      clearNewEntry(type);
      setNotice(`Added “${currentNewEntry.displayText.trim()}” to ${type}.`);
    } catch (err) {
      console.error("Error submitting value:", err);
      if (err.message && err.message.includes("read only property")) {
        setError("Failed to submit value: Unable to update sort order. This might be due to a data integrity issue.");
      } else {
        setError(`Failed to submit value: ${err.message || err}`);
      }
    } finally {
      setPendingAction("");
    }
  };

  // POST Add New Dropdown
  const handleAddDropdown = async (dropdownData) => {
    setPendingAction("bulk-add");
    setError(null);
    setNotice("");
    try {
      await Promise.all(dropdownData.map(async (row) => {
        const postDataObj = {
          dropdownType: row.dropdownType,
          value: row.value,
          displayText: row.displayText,
          sortOrder: row.sortOrder,
          isActive: row.isActive,
        };

        await dispatch(
          portalApi.endpoints.createDropdownValue.initiate({
            url: dropdownValuesUrl,
            data: postDataObj,
          })
        ).unwrap();
      }));
      setShowAddDropdown(false);
      await fetchDropdownValues();
      setNotice(`Created ${dropdownData[0]?.dropdownType || "dropdown"} with ${dropdownData.length} value${dropdownData.length === 1 ? "" : "s"}.`);
    } catch (err) {
      setError(`Error completing dropdown submissions: ${err.message || err}`);
      console.error("Error completing dropdown submissions:", err);
      throw err;
    } finally {
      setPendingAction("");
    }
  };

  const openBulkEditor = (type) => {
    const values = [...(groupedData[type] || [])]
      .sort((left, right) => (
        (Number(left.SortOrder) || Number.MAX_SAFE_INTEGER) -
        (Number(right.SortOrder) || Number.MAX_SAFE_INTEGER)
      ))
      .map((item) => item.DisplayText || "");
    setBulkEditState({ show: true, type, values });
  };

  const closeBulkEditor = () => {
    if (pendingAction === "bulk-edit") return;
    setBulkEditState({ show: false, type: "", values: [] });
  };

  const handleBulkEditDropdown = async (dropdownRows) => {
    const dropdownType = bulkEditState.type;
    const changes = buildBulkDropdownChanges(groupedData[dropdownType] || [], dropdownRows);

    setPendingAction("bulk-edit");
    setError(null);
    setNotice("");
    try {
      await Promise.all(changes.deletes.map((item) => dispatch(
        portalApi.endpoints.deleteDropdownValue.initiate({
          url: dropdownValuesUrl,
          id: item.ID,
        })
      ).unwrap()));

      await Promise.all(changes.updates.map((item) => dispatch(
        portalApi.endpoints.updateDropdownValue.initiate({
          url: dropdownValuesUrl,
          data: item,
        })
      ).unwrap()));

      await Promise.all(changes.creates.map((row) => dispatch(
        portalApi.endpoints.createDropdownValue.initiate({
          url: dropdownValuesUrl,
          data: row,
        })
      ).unwrap()));

      setBulkEditState({ show: false, type: "", values: [] });
      await fetchDropdownValues();
      setNotice(
        `Saved ${dropdownRows.length} ${dropdownType} value${dropdownRows.length === 1 ? "" : "s"}.`
      );
    } catch (err) {
      console.error("Error bulk editing dropdown values:", err);
      setError(`Failed to save all ${dropdownType} values: ${err.message || err}`);
      throw err;
    } finally {
      setPendingAction("");
    }
  };

  // Enable inline editing
  const startEditing = (item, dropdownType) => {
    setEditItem({
      id: item.ID,
      dropdownType,
      displayText: item.DisplayText,
      sortOrder: String(item.SortOrder ?? ""),
      isActive: isDropdownActive(item.IsActive),
    });
  };

  const cancelEditing = () => {
    setEditItem(null);
  };

  const updateEditItem = (updates) => {
    setEditItem((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // PUT: Update existing value
  const handleEdit = async () => {
    if (!editItem) {
      return;
    }
    
    if (!editItem.displayText || editItem.displayText.trim() === "") {
      setError("Display text cannot be empty");
      return;
    }
    
    if (!editItem.sortOrder || isNaN(Number.parseInt(editItem.sortOrder, 10))) {
      setError("Sort order must be a valid number");
      return;
    }
    
    try {
      setPendingAction(`edit:${editItem.id}`);
      setError(null);
      setNotice("");
      const typeItemCount = groupedData[editItem.dropdownType]?.length || 1;
      const resolvedSortOrder = Math.max(
        1,
        Math.min(Number.parseInt(editItem.sortOrder, 10), typeItemCount)
      );
      // Update sort orders and handle any non-sequential sort orders
      const updatedData = await updateSortOrder(
        data,
        resolvedSortOrder,
        editItem.id,
        editItem.dropdownType
      );
      setData(updatedData);

      const updatedItem = {
        ID: editItem.id,
        DropdownType: editItem.dropdownType,
        Value: generateValueFromDisplayText(editItem.displayText),
        DisplayText: editItem.displayText,
        SortOrder: resolvedSortOrder,
        IsActive: editItem.isActive,
      };

      await dispatch(
        portalApi.endpoints.updateDropdownValue.initiate({
          url: dropdownValuesUrl,
          data: updatedItem,
        })
      ).unwrap();
      
      setEditItem(null);
      await fetchDropdownValues();
      setNotice(`Saved “${editItem.displayText.trim()}”.`);
    } catch (err) {
      console.error("Error updating value:", err);
      if (err.message && err.message.includes("read only property")) {
        setError("Failed to update value: Unable to update sort order. This might be due to a data integrity issue.");
      } else {
        setError(`Failed to update value: ${err.message || err}`);
      }
    } finally {
      setPendingAction("");
    }
  };

  // DELETE: Remove value
  const handleConfirmDelete = (item) => {
    setDeleteState({
      show: true,
      id: item.ID,
      displayText: item.DisplayText,
    });
  };

  const handleModalClose = () => {
    setDeleteState({ show: false, id: null, displayText: "" });
  };

  const handleDelete = async () => {
    if (!deleteState.id) {
      return;
    }
    
    try {
      setPendingAction(`delete:${deleteState.id}`);
      setError(null);
      setNotice("");
      await dispatch(
        portalApi.endpoints.deleteDropdownValue.initiate({
          url: dropdownValuesUrl,
          id: deleteState.id,
        })
      ).unwrap();
      
      const deletedDisplayText = deleteState.displayText;
      setDeleteState({ show: false, id: null, displayText: "" });
      await fetchDropdownValues();
      setNotice(`Deleted “${deletedDisplayText}”.`);
    } catch (err) {
      console.error("Error deleting value:", err);
      setError(`Failed to delete value: ${err.message || err}`);
    } finally {
      setPendingAction("");
    }
  };

  const deleteModal = (
    <Modal show={deleteState.show} onHide={handleModalClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Delete dropdown value?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          <strong>{deleteState.displayText}</strong> will be permanently removed from every form that uses this dropdown.
        </p>
        <Alert variant="warning" className="mb-0">
          Consider marking the value inactive if existing records may still reference it.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleModalClose} disabled={Boolean(pendingAction)}>
          Keep value
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={Boolean(pendingAction)}>
          {pendingAction.startsWith("delete:") && <Spinner size="sm" className="me-2" />}
          Delete value
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const filteredGroupedData = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();

    return Object.entries(groupedData).reduce((acc, [type, items]) => {
      const typeMatch = type.toLowerCase().includes(normalized);
      const componentMatch = getDropdownUsage(type).some((usage) => (
        usage.component.toLowerCase().includes(normalized) ||
        usage.area.toLowerCase().includes(normalized)
      ));
      const matchingItems = items.filter((item) => {
        const active = isDropdownActive(item.IsActive);
        if (statusFilter === "active" && !active) return false;
        if (statusFilter === "inactive" && active) return false;
        if (!normalized || typeMatch || componentMatch) return true;

        const displayText = item.DisplayText?.toLowerCase() || "";
        const value = item.Value?.toLowerCase() || "";
        return displayText.includes(normalized) || value.includes(normalized);
      });

      if (matchingItems.length > 0) {
        acc[type] = matchingItems;
      }

      return acc;
    }, {});
  }, [groupedData, searchTerm, statusFilter]);

  const filteredTypeKeys = Object.keys(filteredGroupedData).sort((a, b) => a.localeCompare(b));
  const summary = useMemo(() => {
    const activeValues = data.filter((item) => isDropdownActive(item.IsActive)).length;
    return {
      types: Object.keys(groupedData).length,
      values: data.length,
      active: activeValues,
      inactive: data.length - activeValues,
      visible: Object.values(filteredGroupedData).reduce((total, items) => total + items.length, 0),
    };
  }, [data, filteredGroupedData, groupedData]);

  if (loading) {
    return (
      <div className="dropdown-admin__loading" role="status" aria-live="polite">
        <Spinner animation="border" variant="primary" />
        <div>
          <strong>Loading dropdowns</strong>
          <span>Preparing the configuration workspace...</span>
        </div>
      </div>
    );
  }

  return (
    <section className="dropdown-admin" aria-labelledby="dropdown-admin-title">
      <header className="dropdown-admin__header">
        <div>
          <p className="dropdown-admin__eyebrow">Fields &amp; content</p>
          <h2 id="dropdown-admin-title">Dropdown library</h2>
          <p className="dropdown-admin__description">
            Manage reusable choices, ordering, and availability across portal forms.
          </p>
        </div>
        <div className="dropdown-admin__header-actions">
          <Button
            variant="outline-secondary"
            onClick={() => fetchDropdownValues({ background: true })}
            disabled={refreshing || Boolean(pendingAction)}
          >
            <i className={`bi bi-arrow-clockwise me-2 ${refreshing ? "dropdown-admin__spin" : ""}`} />
            {refreshing ? "Refreshing" : "Refresh"}
          </Button>
          <Button onClick={() => setShowAddDropdown(true)} disabled={Boolean(pendingAction)}>
            <i className="bi bi-plus-lg me-2" />
            New dropdown
          </Button>
        </div>
      </header>

      <div className="dropdown-admin__summary" aria-label="Dropdown summary">
        <div className="dropdown-admin__metric">
          <span>Dropdowns</span>
          <strong>{summary.types}</strong>
        </div>
        <div className="dropdown-admin__metric">
          <span>Total values</span>
          <strong>{summary.values}</strong>
        </div>
        <div className="dropdown-admin__metric dropdown-admin__metric--active">
          <span>Active</span>
          <strong>{summary.active}</strong>
        </div>
        <div className="dropdown-admin__metric dropdown-admin__metric--inactive">
          <span>Inactive</span>
          <strong>{summary.inactive}</strong>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="dropdown-admin__alert">
          <Alert.Heading>We couldn&apos;t complete that action</Alert.Heading>
          <p className="mb-2">{error}</p>
          <Button size="sm" variant="outline-danger" onClick={() => fetchDropdownValues({ background: true })}>
            Try again
          </Button>
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice("")} className="dropdown-admin__alert" aria-live="polite">
          <i className="bi bi-check-circle-fill me-2" />
          {notice}
        </Alert>
      )}

      {showAddDropdown && (
        <AddDropdownModal
          show={showAddDropdown}
          onClose={() => setShowAddDropdown(false)}
          onSubmit={handleAddDropdown}
          isSubmitting={pendingAction === "bulk-add"}
        />
      )}
      {bulkEditState.show && (
        <AddDropdownModal
          show={bulkEditState.show}
          mode="edit"
          initialDropdownType={bulkEditState.type}
          initialValues={bulkEditState.values}
          onClose={closeBulkEditor}
          onSubmit={handleBulkEditDropdown}
          isSubmitting={pendingAction === "bulk-edit"}
        />
      )}
      {deleteState.show && deleteModal}

      <div className="dropdown-admin__workspace">
        <div className="dropdown-admin__toolbar">
          <div className="dropdown-admin__search">
            <i className="bi bi-search" aria-hidden="true" />
            <Form.Control
              type="search"
              placeholder="Search dropdowns, values, or components"
              aria-label="Search dropdowns, values, or components"
              aria-describedby="dropdown-search-help"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="dropdown-admin__clear-search"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
            <span id="dropdown-search-help" className="dropdown-admin__search-help">
              Try a component or area, such as Telemetry, Assembly, or Job schedule.
            </span>
          </div>
          <Form.Group className="dropdown-admin__status-filter">
            <Form.Label htmlFor="dropdown-status-filter">Status</Form.Label>
            <Form.Select
              id="dropdown-status-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All values</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </Form.Select>
          </Form.Group>
          {(searchTerm || statusFilter !== "all") && (
            <Button
              variant="link"
              className="dropdown-admin__reset"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
            >
              Reset filters
            </Button>
          )}
          <span className="dropdown-admin__result-count" aria-live="polite">
            Showing {summary.visible} of {summary.values} values
          </span>
        </div>

        <Accordion
          activeKey={openAccordion}
          onSelect={(eventKey) => setOpenAccordion(eventKey)}
          className="dropdown-admin__accordion"
        >
          {filteredTypeKeys.map((type) => {
            const rowItems = filteredGroupedData[type] || [];
            const allTypeItems = groupedData[type] || [];
            const activeCount = allTypeItems.filter((item) => isDropdownActive(item.IsActive)).length;
            const usage = getDropdownUsage(type);
            const newEntry = getNewEntry(type);

            return (
              <Accordion.Item eventKey={type} key={type}>
                <Accordion.Header>
                  <span className="dropdown-admin__accordion-title">
                    <span>{type}</span>
                    <span className="dropdown-admin__accordion-meta">
                      {rowItems.length !== allTypeItems.length
                        ? `${rowItems.length} matching`
                        : `${allTypeItems.length} values`}
                      <Badge bg={activeCount > 0 ? "success" : "secondary"} pill>
                        {activeCount} active
                      </Badge>
                      <Badge bg={usage.length > 0 ? "info" : "light"} text={usage.length > 0 ? "dark" : "secondary"} pill>
                        {usage.length} component{usage.length === 1 ? "" : "s"}
                      </Badge>
                    </span>
                  </span>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="dropdown-admin__usage">
                    <div className="dropdown-admin__usage-heading">
                      <span>
                        <i className="bi bi-diagram-3 me-2" aria-hidden="true" />
                        Used in
                      </span>
                      <div className="dropdown-admin__usage-actions">
                        <small>Detected from component dropdown references</small>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openBulkEditor(type)}
                          disabled={Boolean(pendingAction)}
                        >
                          <i className="bi bi-pencil-square me-1" />
                          Edit all values
                        </Button>
                      </div>
                    </div>
                    {usage.length > 0 ? (
                      <div className="dropdown-admin__usage-list">
                        {usage.map((item) => (
                          <span className="dropdown-admin__usage-chip" key={item.source} title={item.source}>
                            <strong>{item.component}</strong>
                            <small>{item.area}</small>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="dropdown-admin__usage-empty">
                        No direct component reference was detected. This dropdown may be legacy or loaded dynamically.
                      </p>
                    )}
                  </div>
                  <Form onSubmit={(e) => handleSubmit(e, type)}>
                    <Table responsive hover className="dropdown-admin__table align-middle">
                      <thead>
                        <tr>
                          <th>Display label</th>
                          <th>Stored value</th>
                          <th className="dropdown-admin__order-column">Order</th>
                          <th className="dropdown-admin__status-column">Status</th>
                          <th className="dropdown-admin__actions-column"><span className="visually-hidden">Actions</span></th>
                        </tr>
                      </thead>
                        <tbody>
                        {rowItems.map((item) => (
                          <tr key={item.ID} className={isDropdownActive(item.IsActive) ? "" : "dropdown-admin__inactive-row"}>
                            {editItem?.id === item.ID ? (
                              <>
                                <td>
                                  <Form.Label htmlFor={`dropdown-label-${item.ID}`} className="visually-hidden">
                                    Display label
                                  </Form.Label>
                                  <Form.Control
                                    id={`dropdown-label-${item.ID}`}
                                    type="text"
                                    value={editItem.displayText}
                                    onChange={(e) =>
                                      updateEditItem({ displayText: e.target.value })
                                    }
                                    autoFocus
                                  />
                                </td>
                                <td><code>{generateValueFromDisplayText(editItem.displayText) || "Generated after entry"}</code></td>
                                <td>
                                  <Form.Label htmlFor={`dropdown-order-${item.ID}`} className="visually-hidden">
                                    Sort order
                                  </Form.Label>
                                  <Form.Control
                                    id={`dropdown-order-${item.ID}`}
                                    type="number"
                                    min="1"
                                    value={editItem.sortOrder}
                                    onChange={(e) =>
                                      updateEditItem({ sortOrder: e.target.value })
                                    }
                                  />
                                </td>
                                <td>
                                  <Form.Check
                                    type="switch"
                                    checked={Boolean(editItem.isActive)}
                                    onChange={(e) =>
                                      updateEditItem({ isActive: e.target.checked })
                                    }
                                    label="Active"
                                  />
                                </td>
                                <td className="text-end">
                                  <div className="dropdown-admin__row-actions">
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={handleEdit}
                                    disabled={Boolean(pendingAction)}
                                  >
                                    {pendingAction === `edit:${item.ID}` && <Spinner size="sm" className="me-2" />}
                                    Save
                                  </Button>
                                  <Button
                                    onClick={cancelEditing}
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={Boolean(pendingAction)}
                                  >
                                    Cancel
                                  </Button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td><strong>{item.DisplayText || "Untitled value"}</strong></td>
                                <td><code>{item.Value || "—"}</code></td>
                                <td>{item.SortOrder ?? "—"}</td>
                                <td>
                                  <Badge bg={isDropdownActive(item.IsActive) ? "success" : "secondary"} pill>
                                    {isDropdownActive(item.IsActive) ? "Active" : "Inactive"}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  <div className="dropdown-admin__row-actions">
                                    <Button
                                      size="sm"
                                      onClick={() => startEditing(item, type)}
                                      variant="outline-primary"
                                      disabled={Boolean(pendingAction)}
                                    >
                                      <i className="bi bi-pencil me-1" />
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleConfirmDelete(item)}
                                      disabled={Boolean(pendingAction)}
                                      aria-label={`Delete ${item.DisplayText}`}
                                    >
                                      <i className="bi bi-trash" />
                                    </Button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        <tr className="dropdown-admin__add-row">
                          <td>
                            <Form.Label htmlFor={`new-dropdown-label-${type}`} className="visually-hidden">
                              New display label
                            </Form.Label>
                            <Form.Control
                              id={`new-dropdown-label-${type}`}
                              type="text"
                              placeholder="Add a new value"
                              value={newEntry.displayText}
                              onChange={(e) =>
                                updateNewEntry(type, { displayText: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <code className="dropdown-admin__generated-value">
                              {generateValueFromDisplayText(newEntry.displayText) || "generated-value"}
                            </code>
                          </td>
                          <td>
                            <Form.Label htmlFor={`new-dropdown-order-${type}`} className="visually-hidden">
                              New value sort order
                            </Form.Label>
                            <Form.Control
                              id={`new-dropdown-order-${type}`}
                              type="number"
                              min="1"
                              value={newEntry.sortOrder}
                              onChange={(e) => updateNewEntry(type, { sortOrder: e.target.value })}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="switch"
                              checked={newEntry.isActive}
                              onChange={(e) => updateNewEntry(type, { isActive: e.target.checked })}
                              label="Active"
                            />
                          </td>
                          <td className="text-end">
                            <Button
                              type="submit"
                              size="sm"
                              variant="primary"
                              disabled={!newEntry.displayText.trim() || Boolean(pendingAction)}
                            >
                              {pendingAction === `add:${type}` && <Spinner size="sm" className="me-2" />}
                              Add value
                            </Button>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Form>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
        {filteredTypeKeys.length === 0 && (
          <div className="dropdown-admin__empty">
            <i className="bi bi-search" aria-hidden="true" />
            <h3>No matching dropdown values</h3>
            <p>Try another search or reset the status filter.</p>
            <Button
              variant="outline-primary"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
            >
              Reset filters
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

export default DropdownValues;
